import type { AsapClaims } from '@atlassian/asap-interface';
import jwtDecode from 'jwt-decode';

import { toError } from '@canvas/error';
import { sanitizeMediaId } from '@canvas/media-id';
import {
  CapabilityTier,
  captureExceptionForCapability,
  CustomerImpact,
  type CapabilityInfo,
} from '@canvas/sentry-react';
import type { TokenManager } from '@canvas/token-manager';

const MEDIA_API_BATCH_LIMIT = 255;

const aPersonCanUseMultimedia: CapabilityInfo = {
  capability: {
    importance: CapabilityTier.C2,
    kebabCaseName: 'a-person-can-use-multimedia',
  },
};

export async function associateMediaIdsWithCollection({
  mediaIds,
  tokenManager,
}: {
  mediaIds: readonly string[];
  tokenManager: Pick<TokenManager, 'token'>;
}): Promise<boolean> {
  const uniqueMediaIds = [...new Set(mediaIds.map(sanitizeMediaId))];
  if (uniqueMediaIds.length === 0) {
    return true;
  }

  try {
    const token = await tokenManager.token();
    const algorithm = jwtDecode<{ alg?: string }>(token.mediaToken, { header: true }).alg;
    const issuerHeader = algorithm === 'HS256' ? 'X-Client-Id' : 'x-issuer';
    const issuer = jwtDecode<AsapClaims>(token.mediaToken).iss;
    const collectionId = `contentId-${token.mediaCollectionId}`;
    const baseUrl = token.mediaBaseUrl.endsWith('/')
      ? token.mediaBaseUrl
      : `${token.mediaBaseUrl}/`;
    const url = new URL(`collection/${encodeURIComponent(collectionId)}`, baseUrl);

    for (let index = 0; index < uniqueMediaIds.length; index += MEDIA_API_BATCH_LIMIT) {
      const batch = uniqueMediaIds.slice(index, index + MEDIA_API_BATCH_LIMIT);
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token.mediaToken}`,
          'Content-Type': 'application/json',
          [issuerHeader]: issuer,
        },
        body: JSON.stringify({
          actions: batch.map((id) => ({
            action: 'insert',
            item: { id, type: 'file' },
          })),
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to associate media with collection: HTTP ${response.status}`,
        );
      }
    }

    return true;
  } catch (error) {
    captureExceptionForCapability(
      toError(error),
      {
        tags: {
          captureLocation: 'associateMediaIdsWithCollection-catchAll',
          customerImpact: CustomerImpact.CUSTOMERS,
        },
      },
      [aPersonCanUseMultimedia],
    );
    return false;
  }
}
